/**
 * O BALÃO DESENHADO, QUANDO NÃO HÁ IMAGEM DE BALÃO.
 *
 * ── O PONTO (`T6.5`) ─────────────────────────────────────────────────────────────────────────
 *
 * Ele, na letra: *"Ao salvar, saiu sem formatação de quadrinho, de fala. **Tá só um texto.**"*
 *
 * E é literal — "só um texto" é exatamente o que a tela mostrava. A causa tem duas metades e as
 * duas estavam em `para-o-editor.ts`, na travessia do documento do roteirizador para o formato do
 * editor:
 *
 *   **O QUADRO** virava `{ ...base, type: "rectangles" }`, e o `Rectangle` desenha
 *   `stroke={this.borderColor} strokeWidth={this.borderWidth}`. Nenhum dos dois era passado, então
 *   os dois chegavam `undefined` — **quadro sem contorno**. O documento do roteirizador CARREGA
 *   `traco: 4` e `corTraco: "#000000"`; ninguém os lia.
 *
 *   **O BALÃO** virava `{ ...base, type: "textBubbles" }` com `imgId: ""`. E o `TextBubble` do
 *   editor desenha a forma do balão como **imagem** (`<KonvaImage image={this.getImage()}>`), com a
 *   URL resolvida por `getImgUrl(imgId)`. Sem id, sem imagem; sem imagem, **só o texto**.
 *
 * ── POR QUE SVG, E NÃO UMA IMAGEM NO ACERVO ──────────────────────────────────────────────────
 *
 * O caminho óbvio seria subir PNGs de balão e apontar o `imgId` para eles. Três coisas contra:
 *
 *   1. o acervo de imagem é **por conta** (`users/{uid}/imgs`), então cada criança precisaria da
 *      própria cópia dos balões, gravada na primeira vez que salvasse;
 *   2. o Cloud Storage está fora do plano de cobrança do projeto (`T6.6`, HTTP 402), então imagem
 *      compartilhada não tem onde morar;
 *   3. e o roteirizador **já sabe o estilo** de cada balão — `fala`, `narracao`, `grito`,
 *      `pensamento`, `sussurro` —, informação que `para-o-editor.ts` estava jogando fora. Um PNG
 *      genérico perderia isso de novo.
 *
 * SVG resolve os três de graça: é string, cabe num `data:` URI, entra pelo mesmo caminho de imagem
 * que já existe (nenhuma mudança no Konva), e desenha o estilo certo. É a mesma técnica da retícula
 * do `T4.12` — `trama-do-quadro.ts` —, e pelo mesmo motivo.
 *
 * ── O QUE CADA ESTILO DESENHA, E POR QUE ─────────────────────────────────────────────────────
 *
 * As formas seguem a convenção de quadrinho, que é o que faz a criança ler sem ninguém explicar:
 *
 * | estilo | forma | o que a forma diz |
 * |---|---|---|
 * | `fala` | oval com rabicho para baixo | alguém está falando, e o rabicho aponta quem |
 * | `pensamento` | nuvem, com bolinhas descendo | está pensando, não falando |
 * | `grito` | estrela de pontas irregulares | está gritando |
 * | `sussurro` | oval de traço **interrompido** | está falando baixo |
 * | `narracao` | retângulo de cantos retos, sem rabicho | é o narrador, e não personagem nenhum |
 *
 * O `narracao` ser retângulo **sem rabicho** não é economia de trabalho: rabicho é o que atribui a
 * fala a alguém, e narração não é de ninguém. Um retângulo com rabicho mentiria.
 */

/** Os estilos de balão que o roteirizador produz. Os mesmos de `documento.types.ts`. */
export type EstiloDeBalao = "fala" | "pensamento" | "grito" | "sussurro" | "narracao";

/** A tinta do balão. Preto no traço, branco no fundo — a convenção, e o que dá contraste no texto. */
export const TINTA_DO_BALAO = { traco: "#111111", fundo: "#ffffff" } as const;

/** A espessura do traço, em unidades do próprio SVG. */
export const TRACO_DO_BALAO = 3;

/**
 * O `viewBox` é fixo em 100×100 e o SVG estica.
 *
 * A caixa de um balão vem do roteirizador em pixels de página (336×64, por exemplo), e escrever o
 * path em coordenadas reais exigiria recalculá-lo a cada redimensionamento — que é justamente o
 * que a criança faz arrastando a alça. Com `viewBox` e `preserveAspectRatio="none"`, o navegador
 * estica, e o balão acompanha a caixa sem nós no código.
 *
 * O preço é o traço esticar junto: um balão muito largo fica com borda fina em cima e grossa nos
 * lados. Em 336×64 isso não se vê, e a alternativa — recalcular — trocaria um defeito invisível
 * por um punhado de aritmética que ninguém consegue conferir de cabeça.
 */
const LADO = 100;

/** O `<path>`/`<ellipse>` de cada estilo, dentro do `viewBox` de 100×100. */
function formaDo(estilo: EstiloDeBalao): string {
  const t = TINTA_DO_BALAO;
  const comum = `fill="${t.fundo}" stroke="${t.traco}" stroke-width="${TRACO_DO_BALAO}"`;

  switch (estilo) {
    case "narracao":
      // Retângulo de cantos retos, sem rabicho: o narrador não é personagem.
      return `<rect x="2" y="2" width="96" height="96" ${comum} />`;

    case "fala":
      // Oval mais o rabicho, desenhados juntos num path só — dois elementos separados deixariam o
      // traço do oval cruzando a boca do rabicho.
      return (
        `<path d="M50 3 C22 3 3 18 3 38 C3 55 18 68 40 72 L34 95 L56 71 ` +
        `C79 69 97 56 97 38 C97 18 78 3 50 3 Z" ${comum} />`
      );

    case "pensamento":
      // A nuvem, e as três bolinhas que descem. As bolinhas vão DEPOIS da nuvem para o traço delas
      // ficar por cima.
      return (
        `<path d="M28 26 A15 15 0 0 1 52 16 A16 16 0 0 1 78 28 A13 13 0 0 1 80 54 ` +
        `A15 15 0 0 1 56 64 A16 16 0 0 1 28 60 A14 14 0 0 1 28 26 Z" ${comum} />` +
        `<circle cx="32" cy="74" r="7" ${comum} />` +
        `<circle cx="22" cy="87" r="5" ${comum} />` +
        `<circle cx="14" cy="96" r="3" ${comum} />`
      );

    case "grito":
      // Estrela de pontas IRREGULARES: uma estrela regular lê como selo ou medalha, não como grito.
      return (
        `<path d="M50 2 L60 20 L78 10 L74 30 L96 32 L80 46 L98 60 L76 62 L84 82 ` +
        `L62 74 L56 97 L44 76 L26 90 L28 68 L5 66 L20 50 L3 34 L26 30 L20 12 L38 20 Z" ` +
        `${comum} />`
      );

    case "sussurro":
      // Oval de traço interrompido. `stroke-dasharray` no lugar do traço contínuo, e é a única
      // forma que muda o TRAÇO em vez da silhueta — porque sussurro e fala são a mesma coisa dita
      // mais baixo.
      return (
        `<ellipse cx="50" cy="42" rx="47" ry="39" fill="${t.fundo}" stroke="${t.traco}" ` +
        `stroke-width="${TRACO_DO_BALAO}" stroke-dasharray="7 6" />` +
        `<path d="M40 76 L34 96 L54 78" fill="none" stroke="${t.traco}" ` +
        `stroke-width="${TRACO_DO_BALAO}" stroke-dasharray="7 6" />`
      );
  }
}

/**
 * O SVG de um balão, como texto.
 *
 * Separado do `data:` URI para poder ser conferido por teste sem decodificar base64 — a mesma
 * divisão de `trama-do-quadro.ts`.
 */
export function svgDoBalao(estilo: EstiloDeBalao): string {
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${LADO} ${LADO}" ` +
    `preserveAspectRatio="none">${formaDo(estilo)}</svg>`
  );
}

/**
 * O `data:` URI do balão, que é o que entra no lugar da imagem.
 *
 * `encodeURIComponent` e não base64: o SVG fica legível no inspetor do navegador, o que faz este
 * defeito ser diagnosticável na próxima vez por quem não leu este arquivo. E `#` precisa ser
 * escapado de todo jeito — um `#` cru num `data:` URI corta o resto como fragmento, e a imagem
 * chega truncada sem erro nenhum.
 */
export function urlDoBalao(estilo: EstiloDeBalao): string {
  return `data:image/svg+xml,${encodeURIComponent(svgDoBalao(estilo))}`;
}

/** O estilo é um dos que a gente desenha? Texto de fora não se confia. */
export function ehEstiloDeBalao(v: unknown): v is EstiloDeBalao {
  return v === "fala" || v === "pensamento" || v === "grito" || v === "sussurro" || v === "narracao";
}

/**
 * O estilo a usar, com a queda para `fala`.
 *
 * Por que `fala` e não "nenhum": um balão sem forma é o defeito que este módulo existe para
 * consertar. Um balão com a forma errada é um erro de estilo; um balão sem forma é texto solto no
 * meio do desenho, e foi isso que ele viu.
 */
export function estiloOuFala(v: unknown): EstiloDeBalao {
  return ehEstiloDeBalao(v) ? v : "fala";
}

/**
 * O ESTILO DE UM BALÃO JÁ GRAVADO — T20 (12/10/2026).
 *
 * Ele, na letra: *"depois que eu voltei a imagem, não eram mais recordatórios, eram balões."*
 *
 * O `TextBubble` não gravava `estilo` ao salvar, e ao reabrir todo balão sem imagem caía em
 * `fala`. O conserto grava o estilo daqui em diante; esta função é o que RECUPERA o que já foi
 * salvo sem ele, sem apagar nem reescrever nada no banco — na ordem do que é mais firme:
 *
 *   1. o `estilo` gravado (quadrinho salvo depois do conserto);
 *   2. o `imgId` — o balão posto pela barra do editor grava o NOME do estilo ali;
 *   3. o id `n-<página>-<quadro>`, que o roteirizador dá só ao recordatório;
 *   4. `fala`.
 *
 * O que NÃO se recupera: o balão de pensamento que veio do roteirizador e foi salvo antes do
 * conserto. O id dele (`b-…`) é o mesmo da fala, e nada no documento os separa.
 */
export function estiloDoItem(item: { estilo?: unknown; imgId?: unknown; id?: unknown }): EstiloDeBalao {
  if (ehEstiloDeBalao(item.estilo)) return item.estilo;
  if (ehEstiloDeBalao(item.imgId)) return item.imgId;
  if (typeof item.id === "string" && /^n-\d+-\d+$/.test(item.id)) return "narracao";
  return "fala";
}
